<template>
    <div class="pt-[2px] space-y-4">
        <UTabs v-model="selectedTab" :items="tabs" :ui="tabsUi">
            <template #default="{ item }">
                <span>{{ item.label }}</span>
            </template>
        </UTabs>

        <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-700">{{ tabs[selectedTab].label }}</h2>
            <UButton @click="onAddOption(optionNamespaces[selectedTab])" color="black" icon="i-ph-plus-bold" size="sm">
                Add
            </UButton>
        </div>
        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <div v-if="loading" class="flex items-center justify-center px-6 py-12">
                <span class="text-sm text-gray-400">Loading...</span>
            </div>
            <ul v-else-if="filteredOptions.length" class="divide-y divide-gray-200">
                <li v-for="option in filteredOptions" :key="option.id" class="flex items-center space-x-4 px-5 py-3">
                    <BadgePriority
                        v-if="optionNamespaces[selectedTab] === OptionNamespace.diagnosis && option.metadata?.priority"
                        :priority="option.metadata.priority"
                    />
                    <div class="flex-1 overflow-hidden">
                        <p class="text-gray-800 truncate">{{ option.label }}</p>
                        <p class="text-gray-500 text-sm truncate">{{ option.description }}</p>
                    </div>
                    <UBadge
                        v-if="optionNamespaces[selectedTab] === OptionNamespace.callDecision && option.metadata?.send_admission_message"
                        color="blue"
                        variant="subtle"
                        size="sm"
                        class="flex-shrink-0"
                    >
                        <UIcon name="i-ph-paper-plane-tilt-duotone" class="size-3.5 mr-1" />
                        Auto admission message
                    </UBadge>
                    <UDropdown :items="optionActions(option)" class="flex-shrink-0">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-vertical-20-solid" />
                    </UDropdown>
                </li>
            </ul>
            <div v-else class="flex flex-col items-center justify-center px-6 py-12 space-y-1">
                <UIcon name="i-ph-placeholder-duotone" class="size-5 text-gray-400" />
                <span class="text-gray-400 text-sm">No options.</span>
            </div>
        </div>

        <OverlayOptionCreateOption v-model:open="overlays.createOption" @reload="fetchOptions" :namespace="selectedNamespace" />
        <OverlayOptionEditOption v-model:open="overlays.editOption" @reload="fetchOptions" :option="selectedOption" />
        <OverlayOptionRemoveOption v-model:open="overlays.removeOption" @reload="fetchOptions" :option="selectedOption" />
    </div>
</template>

<script setup lang="ts">
import { useString } from '~/composables/useString'
import { OptionNamespace, type Option } from '~/types/option'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector] })
useHead({ title: 'Settings – Options' })

const { snakeToTitle } = useString()
const client = useSupabaseClient()

const auth = useAuthStore()

const FIELD_POST_NAMESPACES = [OptionNamespace.interventionReason, OptionNamespace.interventionDisposition, OptionNamespace.treatment]

const optionNamespaces = computed(() => Object.values(OptionNamespace)
    .filter(ns => auth.isEnabled('field_posts') || !FIELD_POST_NAMESPACES.includes(ns)))

const tabs = computed(() => optionNamespaces.value.map(ns => ({ label: snakeToTitle(ns) })))

const tabsUi = {
    list: {
        background: 'bg-gray-100',
        marker: { background: 'bg-white shadow-sm' },
    },
}

const selectedTab = ref(0)
const loading = ref(false)
const options = ref<Option[]>([])
const selectedNamespace = ref<OptionNamespace>()
const selectedOption = ref<Option>()

const overlays = reactive({
    createOption: false,
    editOption: false,
    removeOption: false,
})

const filteredOptions = computed(() => {
    return options.value.filter(o => o.namespace === optionNamespaces.value[selectedTab.value])
})

const optionActions = (option: Option) => [[
    {
        label: 'Edit',
        icon: 'i-ph-pencil-fill',
        click: () => {
            selectedOption.value = option
            overlays.editOption = true
        },
    },
    {
        label: 'Remove',
        icon: 'i-ph-trash-fill',
        iconClass: 'text-red-400',
        click: () => {
            selectedOption.value = option
            overlays.removeOption = true
        },
    },
]]

onMounted(() => fetchOptions())

function onAddOption(namespace: OptionNamespace) {
    selectedNamespace.value = namespace
    overlays.createOption = true
}

async function fetchOptions() {
    loading.value = true
    const { data } = await client.from('options').select('*').eq('is_archived', false).order('label')
    if (data) options.value = data
    loading.value = false
}
</script>
